import { useEffect, useState } from 'react';
import {
  CYCLE_LABEL, EU, TIMING_LABEL, VATID_LABEL, amtCur, fmtD, invoiceRows, money, nowYM, plural, r2, rateF,
  svcStats, today0, totals, ymAdd, type InvRow,
} from '@biuro/shared';
import { monthLabel } from '../close.ts';
import { useData, useNav } from '../hooks.tsx';
import { InvoiceForm } from '../forms/InvoiceForm.tsx';
import { ServiceForm } from '../forms/ServiceForm.tsx';
import { InvoiceDrop } from './InvoiceDrop.tsx';
import { Empty, Icon } from '../ui.tsx';

export function statusPill(x: InvRow) {
  const st = x.inv.status || 'new';
  if (st === 'draft') return <span className="pill warn">do uzupełnienia</span>;
  if (x.c.rate == null) return <span className="pill warn">brak kursu</span>;
  if (st === 'booked') return <span className="pill ok">zaksięgowana</span>;
  if (st === 'sent') return <span className="pill">w biurze</span>;
  return <span className="pill hi">do wysłania</span>;
}

export function Faktury() {
  const { sub } = useNav();
  const [tab, setTab] = useState<'faktury' | 'uslugi'>(sub === 'uslugi' ? 'uslugi' : 'faktury');
  const [filter, setFilter] = useState<string>(sub === 'draft' ? 'draft' : '');
  const [editInv, setEditInv] = useState<string | null>(sub === 'new' ? '' : null);
  const [editSvc, setEditSvc] = useState<string | null>(null);
  useEffect(() => {
    if (sub === 'uslugi') setTab('uslugi');
    if (sub === 'draft') { setTab('faktury'); setFilter('draft'); }
    if (sub === 'new') setEditInv('');
  }, [sub]);

  return (
    <>
      <div className="vh">
        <div>
          <h1>Faktury zagraniczne</h1>
          <p>Faktury od zagranicznych dostawców. Kurs NBP i kwotę w złotych aplikacja liczy sama, a faktury trafiają do paczki dla biura.</p>
        </div>
        <div className="actions">
          <button className="btn hi" onClick={() => setEditInv('')}><Icon.plus />Dodaj fakturę</button>
        </div>
      </div>
      <div className="toolbar">
        <div className="seg" role="group" aria-label="Widok">
          <button aria-pressed={tab === 'faktury'} onClick={() => setTab('faktury')}>Faktury</button>
          <button aria-pressed={tab === 'uslugi'} onClick={() => setTab('uslugi')}>Usługi</button>
        </div>
        {tab === 'uslugi'
          ? <button className="btn" onClick={() => setEditSvc('')}><Icon.plus />Dodaj usługę</button>
          : <InvoiceFilter value={filter} onChange={setFilter} />}
      </div>
      {tab === 'uslugi' ? <ServiceList onEdit={setEditSvc} /> : <InvoiceDrop><InvoiceList filter={filter} onEdit={setEditInv} /></InvoiceDrop>}
      {editInv !== null && <InvoiceForm id={editInv || undefined} onClose={() => setEditInv(null)} />}
      {editSvc !== null && <ServiceForm id={editSvc || undefined} onClose={() => setEditSvc(null)} />}
    </>
  );
}

function InvoiceFilter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { data } = useData();
  const months = [...new Set(invoiceRows(data).map((x) => x.c.month).filter(Boolean))];
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label="Filtr">
      <option value="">Wszystkie miesiące</option>
      <option value="draft">Do uzupełnienia</option>
      {[...new Set([...months, nowYM(), ymAdd(nowYM(), -1)])].sort().reverse().map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
    </select>
  );
}

export function InvoiceRows({ rows, onEdit }: { rows: InvRow[]; onEdit: (id: string) => void }) {
  return (
    <div className="panel">
      <div className="inv inv-head"><span>Dostawca i faktura</span><span className="c-hide">Data</span><span className="c-hide num">Kwota</span><span className="c-hide num">Kurs NBP</span><span className="c-hide num">W złotych</span><span className="st">Status</span></div>
      {rows.map((x) => (
        <div key={x.id} className="inv" role="button" tabIndex={0} onClick={() => onEdit(x.id)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onEdit(x.id))}>
          <div>
            <div className="t">
              {x.svc?.name || (x.inv.serviceId ? 'Usługa usunięta' : x.inv.vendor || 'Dostawca nieznany')}
              {!x.inv.serviceId && <span className="tag">inne</span>}
              {x.inv.fileLink && <a className="filelink" href={x.inv.fileLink} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>PDF</a>}</div>
            <div className="s">{[x.inv.number || 'bez numeru', x.c.country].filter(Boolean).join(', ')}</div>
          </div>
          <span className="c-hide amt">{x.inv.paidDate || x.inv.issueDate ? fmtD(x.inv.paidDate || x.inv.issueDate) : '—'}</span>
          <span className="c-hide num">{amtCur(x.inv.amount ?? null, x.c.cur)}</span>
          <span className="c-hide num">{x.c.cur === 'PLN' ? '—' : rateF(x.c.rate)}</span>
          <span className="c-hide num">{money(x.c.net)}</span>
          <span className="st">{statusPill(x)}</span>
          <span className="mvat num muted">{x.c.net != null ? money(x.c.net) : ''}</span>
        </div>
      ))}
    </div>
  );
}

export function Totals({ rows }: { rows: InvRow[] }) {
  const tt = totals(rows.map((x) => x.c));
  return (
    <p className="totals">
      <span>{rows.length} {plural(rows.length, 'faktura', 'faktury', 'faktur')}</span>
      <span>razem netto <b>{money(r2(tt.k27 + tt.k29))}</b></span>
      {tt.missing > 0 && <span>bez kwoty w złotych: {tt.missing}</span>}
    </p>
  );
}

function InvoiceList({ filter, onEdit }: { filter: string; onEdit: (id: string) => void }) {
  const { data } = useData();
  let rows = invoiceRows(data);
  if (filter === 'draft') rows = rows.filter((x) => x.c.draft);
  else if (filter) rows = rows.filter((x) => x.c.month === filter && !x.c.draft);
  rows.sort((a, b) => (b.c.tp?.date || b.inv.issueDate || '').localeCompare(a.c.tp?.date || a.inv.issueDate || ''));
  if (!rows.length) {
    return (
      <div className="panel">
        <Empty title={filter === 'draft' ? 'Nic do uzupełnienia' : 'Brak faktur'}>
          Dodaj fakturę z PDF-em albo wpisz w usługach adresy, z których przychodzą faktury. Wtedy faktury z Gmaila pojawią się tu same. Kurs NBP i kwotę w złotych aplikacja liczy sama.
        </Empty>
      </div>
    );
  }
  // Wszystkie miesiące: grupy według miesiąca rozliczenia, szkice osobno na górze.
  const groups = new Map<string, InvRow[]>();
  for (const x of rows) {
    const k = x.c.draft ? 'draft' : x.c.month || 'none';
    groups.set(k, [...(groups.get(k) || []), x]);
  }
  const keys = [...groups.keys()].sort((a, b) => (a === 'draft' ? -1 : b === 'draft' ? 1 : b.localeCompare(a)));
  return (
    <>
      {keys.map((k) => {
        const arr = groups.get(k)!;
        return (
          <section key={k}>
            <div className="monthhead">
              <h3>{k === 'draft' ? 'Do uzupełnienia' : k === 'none' ? 'Bez daty' : monthLabel(k)}</h3>
              <span className="count">{arr.length}</span>
            </div>
            <InvoiceRows rows={arr} onEdit={onEdit} />
            {k !== 'draft' && k !== 'none' && <Totals rows={arr} />}
          </section>
        );
      })}
    </>
  );
}

function ServiceList({ onEdit }: { onEdit: (id: string) => void }) {
  const { data } = useData();
  const arr = Object.entries(data.services).sort((a, b) => Number(a[1].active === false) - Number(b[1].active === false) || a[1].name.localeCompare(b[1].name, 'pl'));
  if (!arr.length) return <div className="panel"><Empty title="Brak usług">Dodaj zagranicznych dostawców, od których dostajesz faktury z odwrotnym obciążeniem.</Empty></div>;
  let total = 0;
  const cards = arr.map(([id, s]) => {
    const st = svcStats(data, id, today0());
    total += st.sum;
    const country = (s.country || '').toUpperCase();
    const eu = EU.has(country) && country !== 'PL';
    return (
      <div key={id} className={`svc ${s.active === false ? 'off' : ''}`} role="button" tabIndex={0} onClick={() => onEdit(id)} onKeyDown={(e) => e.key === 'Enter' && onEdit(id)}>
        <div><h3>{s.name}</h3><div className="ent">{s.vendor || 'Podmiot do uzupełnienia'}{country ? `, ${country}` : ''}</div></div>
        <div className="chips">
          {country && <span className="tag">{eu ? 'UE' : country === 'PL' ? 'krajowy' : 'spoza UE'}</span>}
          {s.currency && <span className="tag">{s.currency}</span>}
          <span className="tag">{CYCLE_LABEL[s.cycle || ''] || 'cykl do uzupełnienia'}{s.timing ? `, ${TIMING_LABEL[s.timing]}` : ''}</span>
          {s.senders && <span className="tag">z Gmaila</span>}
          {s.verify && <span className="pill warn">do uzupełnienia</span>}
          {s.active === false && <span className="pill">nieaktywna</span>}
        </div>
        <dl className="kv">
          <dt>Ostatnia faktura</dt><dd>{st.last ? fmtD(st.last) : '—'}</dd>
          <dt>Netto z 12 miesięcy</dt><dd>{money(st.sum)}</dd>
          {eu && <><dt>Twój VAT-UE u dostawcy</dt><dd className="plain">{VATID_LABEL[s.vatIdSet || 'unknown']}</dd></>}
        </dl>
        {s.notes && <div className="hint">{s.notes}</div>}
      </div>
    );
  });
  return (
    <>
      <p className="totals" style={{ margin: '0 2px 14px' }}>
        <span>Ostatnie 12 miesięcy netto <b>{money(r2(total))}</b></span>
        <span>średnio <b>{money(r2(total / 12))}</b> miesięcznie</span>
      </p>
      <div className="grid">{cards}</div>
    </>
  );
}
